import React from 'react';
import './PrivacyPolicy.css';

function Section({ title, children }) {
  return (
    <section className="privacy-section">
      <h2 className="privacy-section-title">{title}</h2>
      <div className="privacy-section-content">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <main className="privacy-policy-main">
      <div className="privacy-policy-card">
        <header className="privacy-policy-header">
          <h1 className="privacy-policy-title">PRIVACY POLICY</h1>
          <div className="privacy-policy-meta">
            Effective Date: March 4, 2026 <br />
            Last Updated: June 30, 2026
          </div>
        </header>

        <Section title="Introduction">
          <p>
            This Privacy Policy describes how Instapaw (“Company,” “we,” “our,” or “us”) collects, uses, processes, and protects personal information when you use the Instapaw mobile application (the “App”).
          </p>
          <p>
            Instapaw is a community-driven social platform designed to support animal rescue efforts, including posting rescue updates, adoption listings, and missing animal alerts.
          </p>
          <p>
            By accessing or using Instapaw, you agree to the practices described in this Privacy Policy.
          </p>
        </Section>

        <Section title="1. Scope of This Policy">
          <ul>
            <li>Users who create accounts</li>
            <li>Users who continue as guests or view limited public content</li>
            <li>Users who post animal rescue, adoption, or missing animal content</li>
            <li>Users who create or join Stray Network feeding areas or local animal-care circles</li>
            <li>Users who volunteer as feeders, responders, transporters, NGOs, or local animal-care coordinators</li>
            <li>Users who upload media (images/audio)</li>
            <li>Users who communicate via in-app chat</li>
            <li>Users who enable location-based features</li>
          </ul>
        </Section>

        <Section title="2. Information We Collect">
          <p>We collect only the information necessary to provide, improve, and secure the App.</p>
          <h3 className="privacy-section-subtitle">2.1 Information You Provide Directly</h3>
          <ul>
            <li>Name</li>
            <li>Email address</li>
            <li>Date of birth</li>
            <li>Profile photo (if voluntarily provided)</li>
            <li>Phone number (if voluntarily provided)</li>
            <li>Rescue posts and updates</li>
            <li>Adoption listings</li>
            <li>Missing animal alerts</li>
            <li>Stray Network feeding areas, feeding points, and feeding logs</li>
            <li>Community animal records, including animal condition, notes, and care status</li>
            <li>Vaccination, sterilization, deworming, or other animal-care status that you submit</li>
            <li>Responder, NGO, foster, transporter, or service-area information that you submit</li>
            <li>Uploaded images</li>
            <li>Uploaded audio files</li>
            <li>Chat messages</li>
            <li>Any other information you voluntarily submit</li>
          </ul>
          <p>When posting rescue, adoption or missing animal content, you may provide:</p>
          <ul>
            <li>Animal details</li>
            <li>Location</li>
            <li>Audio recording explaining the situation</li>
            {/* <li>Contact information (if you choose to include it)</li> */}
          </ul>
          <div className="privacy-section-note">You are responsible for ensuring that any information shared publicly is accurate and appropriate.</div>

          <h3 className="privacy-section-subtitle">2.2 Location Information</h3>
          <p>With your permission, we may collect:</p>
          <ul>
            <li>Precise GPS location</li>
            <li>Approximate location</li>
          </ul>
          <p>Location data is used to:</p>
          <ul>
            <li>Display nearby rescue cases</li>
            <li>Find nearby feeding areas and local animal-care activity</li>
            <li>Help match emergency animal alerts with nearby responders or service areas</li>
            <li>Tag adoption or missing animal posts</li>
            <li>Improve coordination within the community</li>
          </ul>
          <div className="privacy-section-note">Location access can be disabled at any time through your device settings. For safety, we may limit, approximate, or hide exact feeding locations, responder locations, routines, or service-area details from public or guest views.</div>

          <h3 className="privacy-section-subtitle">2.3 Media Files</h3>
          <p>If you upload photos or audio:</p>
          <ul>
            <li>Files are securely stored using Firebase services.</li>
            <li>Media is associated with your account and posts.</li>
            <li>Access is controlled via authentication and database security rules.</li>
          </ul>

          <h3 className="privacy-section-subtitle">2.4 Chat Communications</h3>
          <p>If you use in-app messaging:</p>
          <ul>
            <li>Messages and related metadata (timestamps, sender ID, receiver ID) are stored securely.</li>
            <li>Chat data is used solely to provide communication functionality.</li>
            <li>We do not analyze private chat content for advertising purposes.</li>
          </ul>

          <h3 className="privacy-section-subtitle">2.5 Automatically Collected Information</h3>
          <ul>
            <li>Device model and operating system</li>
            <li>App version</li>
            <li>IP address</li>
            <li>Usage patterns</li>
            <li>Crash diagnostics</li>
          </ul>
          <p>We use:</p>
          <ul>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
          </ul>
          <div className="privacy-section-note">These tools help us monitor app performance and improve stability.</div>

          <h3 className="privacy-section-subtitle">2.6 Stray Network and Local Animal-Care Data</h3>
          <p>If you use Stray Network or related local-care features, we may collect and store:</p>
          <ul>
            <li>Feeding-area names, types, approximate or precise locations, and area membership</li>
            <li>Feeding points, risk markers, cleanliness or condition updates, and feeding logs</li>
            <li>Community animal profiles, animal notes, last-seen or last-fed details, and care needs</li>
            <li>Vaccination, sterilization, deworming, health status, proof photos, and related care records</li>
            <li>Emergency alerts, responder service areas, NGO service areas, and volunteer role information</li>
          </ul>
          <div className="privacy-section-note">These records are intended to support community animal welfare coordination. They may be visible to other authorized users depending on the feature, privacy settings, and safety controls in the App.</div>

          <h3 className="privacy-section-subtitle">2.7 Moderation and Account Enforcement Records</h3>
          <p>To protect the community, we may store records related to reports, warnings, suspensions, bans, account deletion actions, enforcement status, expiry dates, dismissal status, and related administrative review metadata.</p>

          <h3 className="privacy-section-subtitle">2.8 Guest and Public Access</h3>
          <p>Guest users may view limited public content without creating an account. Guest access is read-only and may show reduced or approximate information, especially for exact map details, contact options, reporting, joining rescues, commenting, following, and other interactive features.</p>
        </Section>

        <Section title="3. Legal Basis for Processing (Where Applicable)">
          <p>If you are located in jurisdictions such as the European Economic Area (EEA), we process personal data under the following legal bases:</p>
          <ul>
            <li>Performance of a contract (providing App services)</li>
            <li>User consent (permissions such as location, camera, microphone)</li>
            <li>Legitimate interests (security, platform integrity, abuse prevention)</li>
            <li>Legal compliance</li>
          </ul>
        </Section>

        <Section title="4. How We Use Your Information">
          <ul>
            <li>Create and manage user accounts</li>
            <li>Enable rescue, adoption, and missing animal postings</li>
            <li>Operate Stray Network, feeding-area, community animal-care, and emergency-alert coordination features</li>
            <li>Connect local animal-care activity with relevant feeders, responders, NGOs, transporters, fosters, or coordinators</li>
            <li>Facilitate communication between users</li>
            <li>Provide location-based discovery of animals in need</li>
            <li>Limit or approximate sensitive location details to protect feeder, responder, and animal safety</li>
            <li>Maintain and improve App performance</li>
            <li>Prevent fraud, abuse, or harmful activity</li>
            <li>Review reports and administer warnings, restrictions, suspensions, bans, or account deletion actions</li>
            <li>Respond to user inquiries</li>
            <li>Comply with legal obligations</li>
          </ul>
          <div className="privacy-section-note">We do not sell personal data.<br/>We do not use personal data for advertising or behavioral marketing.</div>
        </Section>

        <Section title="5. Third-Party Service Providers">
          <p>We use infrastructure and support services provided by Google LLC through the Firebase platform.</p>
          <p>Firebase services may include:</p>
          <ul>
            <li>Cloud Firestore (database storage)</li>
            <li>Cloud Storage (media storage)</li>
            <li>Authentication services</li>
            <li>Analytics</li>
            <li>Crash reporting</li>
          </ul>
          <p>These providers process data on our behalf and are contractually obligated to protect it in accordance with applicable data protection laws.</p>
          <div className="privacy-section-note">
            Google’s Privacy Policy is available at:<br/>
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a>
          </div>
        </Section>

        <Section title="6. Data Retention">
          <p>We retain personal information:</p>
          <ul>
            <li>For as long as your account remains active</li>
            <li>As necessary to provide services</li>
            <li>As required by law</li>
          </ul>
          <p>Some rescue, Stray Network, feeding-area, community animal-care, emergency-alert, and moderation records may be retained after account deletion where needed to protect animal welfare, preserve community safety, prevent abuse, comply with legal obligations, or maintain the integrity of ongoing local-care records.</p>
          <h3 className="privacy-section-subtitle">Account Deletion</h3>
          <p>Users may delete their account directly within Instapaw. For detailed instructions, see our <a href="/delete-account" style={{color: '#ecb003', textDecoration: 'underline'}}>Delete Account</a> page.</p>
          <p>Upon account deletion:</p>
          <ul>
            <li>Once you delete your account, it will be permanently deactivated, and you will no longer be able to access it or reactivate the same profile. However, certain data such as your profile information, posts, and associated media may not be removed, and chat messages may remain visible to other participants.</li>
            {/* <li>Profile data will not be removed</li>
            <li>Posts and associated media will not be deleted</li>
            <li>Chat messages may remain visible to other participants.</li> */}
          </ul>
          <h3 className="privacy-section-subtitle">Full Data Deletion Request</h3>
          <p>Users may request complete data deletion by contacting:</p>
          <div className="privacy-section-note"><a href="mailto:instapaw.official@gmail.com" target="_blank" rel="noopener noreferrer">instapaw.official@gmail.com</a></div>
          <p>For more information on what data is retained and how to request deletion, visit our <a href="/delete-account" style={{color: '#ecb003', textDecoration: 'underline'}}>Delete Account</a> page. We aim to respond within 30 days, subject to verification and legal requirements.</p>
        </Section>

        <Section title="7. Data Security">
          <p>We implement industry-standard security measures including:</p>
          <ul>
            <li>Encrypted data transmission (HTTPS)</li>
            <li>Firebase security rules</li>
            <li>Authentication-based access controls</li>
            <li>Restricted database permissions</li>
          </ul>
          <div className="privacy-section-note">However, no electronic system is entirely secure.</div>
        </Section>

        <Section title="8. User Responsibilities">
          <p>Because Instapaw is a community-based platform:</p>
          <ul>
            <li>Users are responsible for the content they post.</li>
            <li>Users should avoid sharing sensitive personal information publicly.</li>
            <li>Contact details included in adoption or missing animal posts are shared at the user’s discretion.</li>
          </ul>
          <div className="privacy-section-note">We reserve the right to remove content that violates applicable laws or community guidelines.</div>
        </Section>

        <Section title="9. Permissions Used by the App">
          <p>The App may request the following permissions:</p>
          <ul>
            <li><strong>Camera</strong> – To capture rescue or adoption photos.</li>
            <li><strong>Microphone</strong> – To record audio updates related to rescue efforts.</li>
            <li><strong>Storage / Media Access</strong> – To upload images or audio.</li>
            <li><strong>Location</strong> – To tag rescue or missing animal posts and display nearby activity.</li>
            <li><strong>Notifications</strong> – To send chat alerts, rescue updates, and community notifications.</li>
          </ul>
          <div className="privacy-section-note">Permissions can be revoked at any time via device settings.</div>
        </Section>

        <Section title="10. Children’s Privacy">
          <p>
            Instapaw strictly prohibits any form of child sexual abuse and exploitation (CSAE), including the sharing, solicitation, or possession of child sexual abuse material (CSAM). We do not knowingly collect personal information from children under 18 years of age.
          </p>
          <div className="privacy-section-note">
            If you become aware of any content or behavior that sexually exploits or endangers children, please report it immediately to <a href="mailto:instapaw.official@gmail.com">instapaw.official@gmail.com</a>. We will take appropriate action, including removal of such content and reporting to relevant authorities, in accordance with applicable laws and our policies.
          </div>
        </Section>

        <Section title="11. International Data Transfers">
          <p>Your data may be processed and stored on servers located outside your country through Firebase services. By using Instapaw, you consent to such transfers.</p>
        </Section>

        <Section title="12. Changes to This Privacy Policy">
          <p>We may update this Privacy Policy periodically. Changes will be reflected by updating the “Last Updated” date. Continued use of Instapaw constitutes acceptance of the revised policy.</p>
        </Section>

        <Section title="13. Contact Information">
          <p>If you have questions about this Privacy Policy or wish to exercise your data rights:</p>
          <div className="privacy-section-note">
            Instapaw<br/>
            Email: <a href="mailto:instapaw.official@gmail.com" target="_blank" rel="noopener noreferrer">instapaw.official@gmail.com</a><br/>
            {/* Address: [Insert Business Address]<br/> */}
            Country: India
          </div>
        </Section>

        <footer className="privacy-policy-footer">
          © {new Date().getFullYear()} Instapaw. All rights reserved.
        </footer>
      </div>
    </main>
  );
}
